/*
 * Shop behaviour events (owner brief, 9 Oct 2026; docs/runs/2026-10-09-shop-events.md).
 * dg-product.js and dg-cart.js call DGEvents.track(name, props, options) at the
 * moments the brief names; this file decides whether anything is sent.
 *
 * ONE DESTINATION. Events go to PostHog only, through window.posthog, which
 * snippets/posthog.liquid loads once analytics consent is given. This file
 * names no host and loads nothing. Nothing here reaches the Elevar data layer,
 * dataLayer, or any pixel.
 *
 * CONSENT, READ AT EVERY CALL. An event is sent only while CookieYes reports
 * analytics accepted, read the way the snippet reads it (getCkyConsent(),
 * resolved, categories.analytics literally true), and while PostHog is loaded
 * and not opted out. Refused, it is dropped, never stored. While CookieYes has
 * not yet decided (a page's first moments), or consent is given but PostHog's
 * library has not finished loading, an event is held in memory for up to ten
 * seconds: sent once both are true, dropped if CookieYes decides against
 * analytics or the time runs out. Nothing is sent before the decision.
 *
 * NEVER FROM A SEALED OR SENSITIVE REGION. A caller passes the element the
 * event came from as options.origin; inside [data-dg-sensitive] or a sealed
 * host ([data-dg-sealed]) nothing is sent. Marketing-site
 * docs/decisions/2026-10-06-sealed-regions.md and
 * 2026-09-16-posthog-autocapture-scoped-exclusion.md.
 *
 * WHAT AN EVENT CARRIES. The caller's properties, which are fixed words,
 * true/false or numbers, never a name, an email, a code or typed text; and two
 * added here: page_kind (the template: product, cart, page, ...) and
 * width_band (phone below 990, desktop from 990). Which product is identified
 * by the page address PostHog records with every event, not by a property.
 *
 * options.beacon sends the event by sendBeacon, for a moment followed at once
 * by a page change (a checkout, or /cart's reload after a change).
 *
 * DGEvents.carry(value) and DGEvents.carried() keep one small value across
 * /cart's own reload (sessionStorage, written only while analytics consent is
 * given, read once and removed, and ignored when older than 15 seconds): the
 * offers already reported in that cart view, so a reload does not count them
 * again. It holds fixed words only.
 */
(() => {
  const WAIT = 10000;
  const CARRY = 'dg_cart_view';
  const FRESH = 15000;
  const wide = window.matchMedia('(min-width: 990px)');
  const held = [];
  let timer = 0;
  let since = 0;

  function readCky() {
    if (typeof window.getCkyConsent !== 'function') return null;
    try {
      return window.getCkyConsent() || null;
    } catch {
      return null;
    }
  }

  // CookieYes has decided: its reading carries the law or the language.
  function resolved() {
    const state = readCky();
    return Boolean(state && (state.activeLaw || state.languageCode));
  }

  function granted() {
    const state = readCky();
    return resolved() && Boolean(state.categories && state.categories.analytics === true);
  }

  function ready() {
    const posthog = window.posthog;
    if (!posthog || !posthog.__loaded || typeof posthog.capture !== 'function') return false;
    try {
      return !(posthog.has_opted_out_capturing && posthog.has_opted_out_capturing());
    } catch {
      return false;
    }
  }

  function sealed(origin) {
    return origin instanceof Element && Boolean(origin.closest('[data-dg-sensitive], [data-dg-sealed]'));
  }

  function send(item) {
    try {
      window.posthog.capture(item.name, item.props, item.beacon ? { transport: 'sendBeacon' } : undefined);
    } catch {
      // Analytics never breaks the page.
    }
  }

  function stop() {
    window.clearInterval(timer);
    timer = 0;
  }

  function flush() {
    if ((resolved() && !granted()) || Date.now() - since > WAIT) {
      held.length = 0;
      stop();
      return;
    }
    if (!granted() || !ready()) return;
    while (held.length) send(held.shift());
    stop();
  }

  function track(name, props = {}, options = {}) {
    if (sealed(options.origin) || (resolved() && !granted())) return;
    const template = document.getElementById('MainContent')?.dataset.template || 'other';
    const item = {
      name,
      props: { ...props, page_kind: template, width_band: wide.matches ? 'desktop' : 'phone' },
      beacon: Boolean(options.beacon),
    };
    if (granted() && ready()) {
      send(item);
      return;
    }
    held.push(item);
    if (!timer) {
      since = Date.now();
      timer = window.setInterval(flush, 250);
    }
  }

  function carry(value) {
    if (!granted()) return;
    try {
      window.sessionStorage.setItem(CARRY, JSON.stringify({ at: Date.now(), value }));
    } catch {
      // Without storage a reload counts as a new view.
    }
  }

  function carried() {
    try {
      const value = window.sessionStorage.getItem(CARRY);
      window.sessionStorage.removeItem(CARRY);
      const entry = value ? JSON.parse(value) : null;
      // Only the reload that wrote it: anything older is a new visit.
      return entry && Date.now() - entry.at < FRESH ? entry.value : null;
    } catch {
      return null;
    }
  }

  window.DGEvents = { track, carry, carried };
})();
